<template>
  <div>
    <div
      v-if="isLoading"
      class="section"
    >
      <div class="loader" />
    </div>

    <template v-else>
      <div class="section section--shadow center">
        <div class="container max-width-tablet">
          <h1 class="title title--2 primary">{{ name }}</h1>
          <formatted-content
            class="bigger"
            :content="description"
          />
        </div>
      </div>

      <div class="section">
        <div class="layout layout--grid">
          <div
            v-for="(location, index) in locations"
            :key="location.store.storeId"
            class="box"
            :data-test-id="`location-card-${index+1}`"
          >
            <div
              v-if="location.brandingOptions.logoUrl"
              class="box__header-image box__header-image--wrapper"
              :style="`background-color: ${location.brandingOptions.skinOptions.headerBackgroundColor};`"
            >
              <img
                :src="location.brandingOptions.logoUrl"
                :alt="location.store.description"
              >
            </div>

            <h2 class="title title--3 primary">
              {{ location.title }}
            </h2>

            <div class="layout layout--icon">
              <span class="icon far fa-clock gray" />

              <p class="bold smaller no-wrap">
                <current-day-store-hours :location="location.store" />
              </p>
            </div>

            <p v-if="location.description">{{ location.description }}</p>

            <div class="layout layout--horizontal">
              <button
                title="View store details"
                type="button"
                class="button button--icon button--light"
                @click="$openModal('locationDetails', {
                  extraInfo: location.info,
                  hideOrderingModes: true,
                  location: location.store
                })"
              >
                <span class="icon fa-info" />
              </button>

              <button
                type="button"
                class="button button--primary button--auto-width"
                @click="selectLocation(location, $event)"
              >
                Order Now!
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>



<script>
  import errorHandlers from '@/services/error-handlers';
  import http from '@/services/http';
  import parseLocation from '@/helpers/parse-location';
  import { mapGetters } from 'vuex';



  export default {
    name: 'ParentLandingPage',

    metaInfo() {
      return {
        title: 'Welcome'
      };
    },

    data() {
      return {
        brandingMerchantId: null,
        description: null,
        isLoading: false,
        locations: [],
        name: null
      };
    },

    computed: {
      ...mapGetters('merchant', ['merchant'])
    },

    created() {
      this.createdHandler();
    },

    methods: {
      createdHandler() {
        this.loadLandingPage();
      },

      async loadLandingPage() {
        try {
          this.isLoading = true;

          const response = await http.get(`/cardfree-api/v1/landing-pages/${this.$route.params.token}`);

          this.brandingMerchantId = response.data.brandingMerchantId;
          this.description = response.data.description;
          this.name = response.data.name;

          this.locations = response.data.locations.map((location) => {
            const store = parseLocation({
              location: location.store
            });

            store.description = location.title;

            return {
              ...location,
              store
            };
          });

          this.validateMerchant();
        }

        catch (error) {
          errorHandlers.critical(error);
        }

        finally {
          this.isLoading = false;
        }
      },

      selectLocation(location, event) {
        try {
          const route = {
            query: {
              ...this.$route.query
            },
            params: {
              merchantId: location.friendlyUriName
            }
          };

          route.query.ml = location.store.merchantLocationId;
          route.query.ru = window.location.href;

          if (location.linkType === 'OrderAhead') {
            route.name = 'autoStartOrder';
            route.query.ft = 'pick-up';
          }

          else if (location.linkType === 'OrderAheadDelivery') {
            route.name = 'autoStartOrder';
            route.query.ft = 'delivery';
          }

          else if (location.linkType === 'OrderAheadDineIn') {
            route.name = 'autoStartOrder';
            route.query.ft = 'dine-in';
          }

          else if (location.linkType === 'OrderAheadRoomService') {
            route.name = 'autoStartOrder';
            route.query.ft = 'room-service';
          }

          else if (location.linkType === 'OrderAheadShipping') {
            route.name = 'autoStartOrder';
            route.query.ft = 'shipping';
          }

          else if (location.linkType === 'OrderAtTable') {
            route.name = 'orderAtTable';
          }

          else {
            throw new Error(`Selected linkType "${location.linkType}" is not supported.`);
          }

          const host = location.baseUrl.replace(/\/$/, '');
          const resolvedRoute = this.$router.resolve(route);
          const url = host + resolvedRoute.href;

          /*
            NOTE: Since the merchant is changing, we have to reload the page so the BE can inject the
            new merchant data. This is why we can't use $router.push()
          */

          if (event.metaKey || event.ctrlKey) {
            window.open(url, '_blank');
          }

          else {
            window.location.href = url;
          }
        }

        catch (error) {
          errorHandlers.generic(error);
        }
      },

      validateMerchant() {
        if (this.merchant.internalId !== this.brandingMerchantId) {
          throw new Error('Merchant ID mismatch.');
        }
      }
    }
  };
</script>
